// Крупный выигрыш поверх игры: затемнение, манометр Big Win и сумма на его плите.
//
// Плита вылетает снизу, садится на место и пару секунд стоит: бьётся стрелка манометра,
// трещит стекло, ходят блики. Дальше попап уходит сам, см. GDD, раздел 3.7 и ADR-56.
//
// Игрока попап не ждёт и кнопки не несёт. За ним ту же сумму называют створки поля со своим
// счётчиком, см. ui/fieldCover.ts, и держать игрока дважды незачем.
//
// Надпись и сумму игра рисует сама: художник оставил под них по чёрному квадрату, как
// и на створках. Ставит она их своим узлом, а не узлом слота: слот Spine ведёт костью плиты,
// а кость эта лежит повёрнутой на девяносто градусов, и надпись на ней встала бы боком.
// Место надписей снято из скелета, см. BIG_WIN_LABELS.

import { Container, Graphics, Text, type TextStyleOptions } from 'pixi.js';

import type { Cents } from '../api/gameConfig';
import { BIG_WIN_ART, BIG_WIN_LABELS, BIG_WIN_PLATE, BIG_WIN_SKELETON, TITLE_FONT } from '../assets/catalog';
import type { AssetManager, Spine } from '../assets/manager';
import { textFamily } from '../i18n/text';

import { AmountLabel } from './amount';
import { fitLabel } from './label';
import { animationSeconds, DESIGN_HEIGHT, type Viewport } from './scene';

/** Насколько затемнение гасит игру под собой. Оно то же, что у попапов и у сообщения об ошибке. */
const DIM_ALPHA: number = 0.75;

/**
 * Какую долю высоты макета занимает рисунок. Остаток — поля сверху и снизу.
 *
 * Влёт разгоняет плиту до 1.1 своего размера, и запас взят под него: в полный экран рисунок
 * выехал бы за края как раз на этом кадре.
 */
const ART_SHARE: number = 0.9;

/**
 * Сколько попап стоит целиком: влёт кончился, сумма на плите, уход ещё не начался.
 *
 * Считается это время от конца влёта, а не от начала показа. Влёт идёт секунду, и вычти
 * его из общего срока — сумма мелькала бы: она встаёт как раз к его концу.
 */
const HOLD_SECONDS: number = 1.6;

/** За сколько попап набирает густоту и за сколько уходит. */
const FADE_SECONDS: number = 0.35;

// Надпись и сумма меряются в координатах скелета, как и места под них: их узел стоит на плите
// и с ней же ужимается, см. BIG_WIN_PLATE.

/** Высота кегля надписи. Взята с запасом: надпись ужимается под поле плиты. */
const CAPTION_SIZE: number = 120;

/** Высота цифры суммы. Сумма стоит на такой же плите, что и надпись. */
const AMOUNT_HEIGHT: number = 120;

/** Надпись на плите: золото с тёмной обводкой, как на референсе в GDD, раздел 3.7. */
function captionStyle(family: string): TextStyleOptions {
  return {
    fontFamily: textFamily(family),
    fontSize: CAPTION_SIZE,
    fontWeight: 'bold',
    fill: 0xd6a02c,
    stroke: { color: 0x40210f, width: CAPTION_SIZE / 10, join: 'round' },
    align: 'center',
    letterSpacing: 6,
  };
}

/** Показ крупного выигрыша поверх поля. */
export class BigWin {
  readonly view: Container = new Container();

  readonly #dim: Graphics = new Graphics();
  readonly #art: Spine;
  readonly #caption: Text;
  readonly #amount: AmountLabel;

  /** Узел надписи и суммы. Он встаёт на плиту, когда та долетела. */
  readonly #labels: Container = new Container();

  /** Сколько идёт влёт плиты. До его конца надписей на ней нет. */
  readonly #introSeconds: number;

  /** Сколько попап держится на экране: влёт, стойка после него и уход. */
  readonly #shownSeconds: number;

  #seconds = 0;

  /** Кто ждёт конца показа. Будит его сам показ. */
  #onFinished: (() => void) | null = null;

  constructor(assets: AssetManager) {
    this.#art = assets.spine(BIG_WIN_SKELETON);

    const scale: number = (DESIGN_HEIGHT * ART_SHARE) / BIG_WIN_ART.height;

    // Рисунок стоит ниже начала координат скелета, поэтому по вертикали он сдвигается
    // на свою середину. Ось Y у Spine смотрит вверх, у Pixi вниз, отсюда и знак.
    this.#art.scale.set(scale);
    this.#art.position.set(0, BIG_WIN_ART.centerY * scale);

    this.#introSeconds = animationSeconds(this.#art, BIG_WIN_SKELETON.animations.intro);
    this.#shownSeconds = this.#introSeconds + HOLD_SECONDS + FADE_SECONDS;

    this.#caption = new Text({ text: '', style: captionStyle(assets.font(TITLE_FONT)) });
    this.#caption.anchor.set(0.5);
    this.#caption.position.set(BIG_WIN_LABELS.caption.x, -BIG_WIN_LABELS.caption.y);

    this.#amount = new AmountLabel(assets, AMOUNT_HEIGHT);
    this.#amount.view.position.set(BIG_WIN_LABELS.amount.x, -BIG_WIN_LABELS.amount.y);

    // Узел надписей стоит на рисунке и мерится его координатами: места заготовок сняты в них.
    // Ось Y у Spine смотрит вверх, у Pixi вниз, поэтому места идут с минусом.
    this.#labels.scale.set(scale);
    this.#labels.position.copyFrom(this.#art.position);
    this.#labels.addChild(this.#caption, this.#amount.view);

    // Затемнение забирает нажатия себе: пока попап на экране, игра их не получает.
    this.#dim.eventMode = 'static';

    this.view.visible = false;
    this.view.addChild(this.#dim, this.#art, this.#labels);
  }

  /**
   * Показывает сумму и ждёт, пока показ уйдёт сам.
   *
   * Второй показ обрывает первый: двух крупных выигрышей разом не бывает, а брошенное
   * ожидание не кончилось бы никогда.
   *
   * @param caption Надпись над манометром. Она идёт как есть, поэтому регистр задаёт зовущий.
   */
  show(caption: string, total: Cents): Promise<void> {
    this.#wake();

    this.#caption.text = caption;
    fitLabel(this.#caption, { width: BIG_WIN_PLATE.width, height: BIG_WIN_PLATE.height });

    this.#amount.clear();
    this.#amount.draw(total);

    // Надписи ждут конца влёта: плита в эти секунды то мелкая, то на полпути к своему месту.
    this.#labels.visible = false;

    this.#seconds = 0;
    this.view.alpha = 0;
    this.view.visible = true;

    this.#art.state.setAnimation(0, BIG_WIN_SKELETON.animations.intro, false);
    this.#art.state.addAnimation(0, BIG_WIN_SKELETON.animations.loop, true, 0);

    return new Promise<void>((resolve) => {
      this.#onFinished = resolve;
    });
  }

  /** Проходит показ разом: игрок торопит раунд, и досматривать плиту он не будет. */
  skip(): void {
    if (this.view.visible)
      this.#hide();
  }

  update(seconds: number): void {
    if (!this.view.visible)
      return;

    this.#seconds += seconds;

    if (this.#seconds >= this.#shownSeconds) {
      this.#hide();
      return;
    }

    this.#labels.visible = this.#seconds >= this.#introSeconds;

    // Густоту попап набирает на входе и теряет на выходе, между ними держится единица.
    this.view.alpha = Math.min(this.#seconds, this.#shownSeconds - this.#seconds, FADE_SECONDS) / FADE_SECONDS;
  }

  /** Растягивает затемнение на всю видимую область: под попапом не должно остаться игры. */
  layout(viewport: Viewport): void {
    this.#dim.clear();
    this.#dim.rect(-viewport.width / 2, -viewport.height / 2, viewport.width, viewport.height);
    this.#dim.fill({ color: 0x000000, alpha: DIM_ALPHA });
  }

  /** Снимает попап с экрана и будит того, кто ждал его конца. */
  #hide(): void {
    this.view.visible = false;
    this.#labels.visible = false;

    this.#wake();
  }

  /** Будит того, кто ждёт показ. Второй раз будить некого. */
  #wake(): void {
    const finished: (() => void) | null = this.#onFinished;

    this.#onFinished = null;
    finished?.();
  }
}
